/**
 * Toast.tsx — Conversion Architect shared toast.
 *
 * Fixed bottom-center pill. Replaces every alert() in the CA module.
 * Auto-clears after ~4.5s; can also be dismissed with the ✕ button.
 */

import React, { useEffect } from 'react';
import { c, font } from './tokens';

const AUTO_CLEAR_MS = 4500;

interface ToastProps {
  message: string;
  onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({ message, onClose }) => {
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(onClose, AUTO_CLEAR_MS);
    return () => clearTimeout(t);
  }, [message, onClose]);

  if (!message) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: 28,
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        background: c.green.deep,
        color: '#fff',
        borderRadius: 10,
        padding: '12px 18px',
        fontFamily: font.ui,
        fontSize: 13,
        fontWeight: 600,
        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.35)',
        zIndex: 1000,
        maxWidth: 520,
      }}
    >
      <span>{message}</span>
      <button
        type="button"
        aria-label="Dismiss notification"
        onClick={onClose}
        style={{
          background: 'transparent',
          border: 'none',
          color: '#fff',
          fontFamily: font.ui,
          fontSize: 14,
          fontWeight: 700,
          cursor: 'pointer',
          padding: 0,
          lineHeight: 1,
        }}
      >
        ✕
      </button>
    </div>
  );
};

export default Toast;
